import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.symbol.description.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/es.string.replace.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/es.string.search.js";
import "core-js/modules/web.dom-collections.iterator.js";
import "core-js/modules/web.url-search-params.js";
import "regenerator-runtime/runtime.js";
import { defineComponent as _defineComponent } from 'vue';
import PButton from 'primevue/button';
import BImageWithBlurredBackground from '~/components/general/BImageWithBlurredBackground.vue';
import EventInfoPreview from '~/components/Event/EventInfoPreview.vue';

// SSR data (`event`), the SEO head() (meta + JSON-LD), `mainImageUri` and
// goToBuyTickets live in the companion <script> block above — see the comment
// there for why. This block owns only the client-reactive UI.

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * Why this SFC has BOTH a <script> and a <script setup lang="ts"> block
 * ─────────────────────────────────────────────────────────────────────────────
 * This is the public SSR landing page for an external / affiliate event. Its
 * entire server-side job is the <head> — the og:/twitter: meta and the
 * JSON-LD that crawlers read. The visible body sits inside <client-only> and
 * never server-renders.
 *
 * On the current stack (@nuxt/bridge, `bridge.nitro: false`, Vue 2.7, the
 * legacy @nuxt/vue-renderer) the ONLY mechanism that both (a) populates data
 * before vue-meta reads the head and (b) re-evaluates the head at inject time
 * is the Options API `fetch()` + `head()`. Every <script setup> alternative
 * was tried and does not work here:
 *   • useAsyncData / useFetch / useLazyAsyncData never run on the server —
 *     bridge gates their server branch behind `payload.serverRendered`, which
 *     is only set under Nitro (see @nuxt/bridge runtime/composables/asyncData.mjs).
 *   • onServerPrefetch does fetch on the server, but useNuxt2Meta snapshots
 *     the head synchronously inside setup() — it is a
 *     `watch(fn, cb, { immediate: true })` that writes vm.$metaInfo once,
 *     before the prefetch resolves — and the server-side watcher does not
 *     re-run, so the meta ships with empty `content`
 *     (see @nuxt/bridge runtime/nuxt.mjs → useNuxt2Meta).
 *   • `bridge.meta: true` / useHead (unhead) only injects SSR head through the
 *     Nitro renderer; the legacy renderer builds <head> purely from
 *     vue-meta's `renderContext.meta.inject()` and ignores unhead.
 *
 * So the SSR data + SEO stay Options API in this block (defineComponent, so
 * `this` is fully typed — `$store` reaches it via the ComponentCustomProperties
 * augmentation in types/vue-shim.d.ts), lifted from the pre-migration file. The
 * former `jsonld()` option is folded into `head()` as a `script` entry —
 * identical output, and it drops the nuxt-jsonld dependency for this page. Two
 * loose spots remain, both genuine upstream type gaps, not laziness:
 *   • `event` is left untyped — its concrete type (ExternalEvent) is not
 *     re-exported from the @tickethead/sdk package root (same as other Options
 *     pages, e.g. holders.vue).
 *   • `getEvent()` casts the response `as any` — the SDK types
 *     getExternalEvent() as Promise<ExternalEvent> but it resolves to the raw
 *     { data: ExternalEvent } envelope at runtime.
 * The reactive UI is migrated to <script setup lang="ts"> below; the SFC
 * compiler merges the two (<script setup> → a setup() injected onto this
 * default export), and `fetch()` populates a reactive `event` data property
 * that both blocks and the template read.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * STAGE B — after the Nuxt 3 flip (Nitro on, unhead active)
 * ─────────────────────────────────────────────────────────────────────────────
 * Delete this entire <script> block and fold its logic into <script setup>:
 *   • data()/fetch()/getEvent() → top-level
 *       const { data: event } = await useAsyncData('external-event', () =>
 *         sdk.aggregator.getExternalEvent({ id: eventId, provider }).then((r) => {
 *           const e = (r as any).data;
 *           e.venue = { ...e.venue, ...e.venue.address };
 *           return e;
 *         }));
 *   • head() → useHead({ meta: [...], script: [...] }) — both arrays already
 *     have the exact shape unhead wants, so this is a near-verbatim move.
 *   • mainImageUri → a computed() in setup; goToBuyTickets → a plain function.
 */
import { defineComponent } from 'vue';
import { mapToLocationJsonLd, mapToOrganizerJsonLd, mapToEventJsonLd } from '~/utils/json-ld';
import { useAppStoreDefinition } from '~/store/app';
var __default__ = defineComponent({
  data: function data() {
    return {
      event: null,
      eventId: this.$route.params.id,
      provider: this.$route.params.provider
    };
  },
  fetch: function fetch() {
    var _this = this;
    return _asyncToGenerator(/*#__PURE__*/regeneratorRuntime.mark(function _callee() {
      var appStore, _e$response, _e$response$data, _t;
      return regeneratorRuntime.wrap(function (_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            appStore = useAppStoreDefinition(_this.$pinia);
            _context.prev = 1;
            appStore.setLoading(true);
            _context.next = 2;
            return _this.getEvent();
          case 2:
            _context.next = 4;
            break;
          case 3:
            _context.prev = 3;
            _t = _context["catch"](1);
            _this.$notifier.showMessage({
              content: (_t === null || _t === void 0 ? void 0 : (_e$response = _t.response) === null || _e$response === void 0 ? void 0 : (_e$response$data = _e$response.data) === null || _e$response$data === void 0 ? void 0 : _e$response$data.message) || _this.$t('errors.fetchEvent'),
              type: 'error'
            });
          case 4:
            _context.prev = 4;
            appStore.setLoading(false);
            return _context.finish(4);
          case 5:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[1, 3, 4, 5]]);
    }))();
  },
  head: function head() {
    var _this$event, _this$event2, _this$event3, _this$event4, _this$event5, _this$event6, _this$event7, _this$event8;
    return {
      meta: [{
        hid: 'og:title',
        property: 'og:title',
        content: (_this$event = this.event) === null || _this$event === void 0 ? void 0 : _this$event.name
      }, {
        hid: 'og:site_name',
        property: 'og:site_name',
        content: this.$t('page.title')
      }, {
        hid: 'og:image',
        property: 'og:image',
        content: (_this$event2 = this.event) === null || _this$event2 === void 0 ? void 0 : _this$event2.mainImageUri.replace('16x9', '4x3')
      }, {
        hid: 'og:image:url',
        property: 'og:image:url',
        content: (_this$event3 = this.event) === null || _this$event3 === void 0 ? void 0 : _this$event3.mainImageUri.replace('16x9', '4x3')
      }, {
        hid: 'og:image:type',
        property: 'og:image:type',
        content: 'image/webp'
      }, {
        hid: 'og:image:secure_url',
        property: 'og:image:secure_url',
        content: (_this$event4 = this.event) === null || _this$event4 === void 0 ? void 0 : _this$event4.mainImageUri.replace('16x9', '4x3')
      }, {
        hid: 'og:description',
        property: 'og:description',
        content: (_this$event5 = this.event) === null || _this$event5 === void 0 ? void 0 : _this$event5.description
      }, {
        hid: 'twitter:card',
        property: 'twitter:card',
        content: 'summary'
      }, {
        hid: 'twitter:title',
        property: 'twitter:title',
        content: (_this$event6 = this.event) === null || _this$event6 === void 0 ? void 0 : _this$event6.name
      }, {
        hid: 'twitter:description',
        property: 'twitter:description',
        content: (_this$event7 = this.event) === null || _this$event7 === void 0 ? void 0 : _this$event7.description
      }, {
        hid: 'twitter:image',
        property: 'twitter:image',
        content: (_this$event8 = this.event) === null || _this$event8 === void 0 ? void 0 : _this$event8.mainImageUri.replace('16x9', '4x3')
      }],
      // Former jsonld() option, inlined as a head <script> entry — identical
      // rendered <script type="application/ld+json">. Pre-stringified via
      // `innerHTML` because vue-meta's `json` key expects a JsonObj (string
      // index signature) that the repo's hand-rolled EventJsonLd lacks.
      // TODO: add startAt time to external events so they are viable for this.
      script: this.event ? [{
        hid: 'jsonld',
        type: 'application/ld+json',
        innerHTML: JSON.stringify(mapToEventJsonLd(this.event, this.$route.fullPath, mapToLocationJsonLd(this.event.venue), mapToOrganizerJsonLd(this.event.organizer)))
      }] : []
    };
  },
  computed: {
    mainImageUri: function mainImageUri() {
      if (this.event.mainImageUri) {
        return this.event.mainImageUri;
      } else {
        return require('assets/img/featured_event.webp');
      }
    }
  },
  methods: {
    goToBuyTickets: function goToBuyTickets() {
      window.location.href = this.event.externalUrl;
    },
    getEvent: function getEvent() {
      var _this2 = this;
      return _asyncToGenerator(/*#__PURE__*/regeneratorRuntime.mark(function _callee2() {
        var res, _e$response2, _e$response2$data, _t2;
        return regeneratorRuntime.wrap(function (_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              _context2.prev = 0;
              _context2.next = 1;
              return _this2.$sdk.aggregator.getExternalEvent({
                id: _this2.eventId,
                provider: _this2.provider
              });
            case 1:
              res = _context2.sent;
              _this2.event = res.data;
              _this2.event.venue = _objectSpread(_objectSpread({}, _this2.event.venue), _this2.event.venue.address);
              _context2.next = 3;
              break;
            case 2:
              _context2.prev = 2;
              _t2 = _context2["catch"](0);
              _this2.$notifier.showMessage({
                content: (_t2 === null || _t2 === void 0 ? void 0 : (_e$response2 = _t2.response) === null || _e$response2 === void 0 ? void 0 : (_e$response2$data = _e$response2.data) === null || _e$response2$data === void 0 ? void 0 : _e$response2$data.message) || _this2.$t('errors.fetchEvent'),
                type: 'error'
              });
            case 3:
            case "end":
              return _context2.stop();
          }
        }, _callee2, null, [[0, 2]]);
      }))();
    }
  }
});
const __nuxt_page_meta = {
  layout: 'default_v2'
}
export default /*#__PURE__*/_defineComponent(_objectSpread(_objectSpread({}, __default__), {}, {
  ...__nuxt_page_meta,__name: 'index',
  setup: function setup(__props) {
    ;
    var _useBreakpoint = useBreakpoint(),
      xsOnly = _useBreakpoint.xsOnly;
    var _useI18n = useI18n(),
      t = _useI18n.t;
    var currentTab = ref(0);
    var tabs = [{
      label: t('main.tabs.eventInfo'),
      name: 'name'
    }];
    var config = reactive({
      eventDescription: true
    });
    var isMobile = computed(function () {
      return xsOnly.value;
    });
    onMounted(function () {
      var urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('description') === 'false') {
        config.eventDescription = false;
      }
    });
    return {
      __sfc: true,
      xsOnly: xsOnly,
      t: t,
      currentTab: currentTab,
      tabs: tabs,
      config: config,
      isMobile: isMobile,
      PButton: PButton,
      BImageWithBlurredBackground: BImageWithBlurredBackground,
      EventInfoPreview: EventInfoPreview
    };
  }
}));